import { useEffect, useState } from "react";
import { Users2, Clock, AlertCircle, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";

interface RepRow {
  user_id: string;
  full_name: string | null;
  email: string | null;
  last_seen_at: string | null;
  days_since: number | null;
}

function formatLastSeen(d: string | null): { label: string; tone: "ok" | "warn" | "stale" } {
  if (!d) return { label: "Never logged in", tone: "stale" };
  const ms = Date.now() - new Date(d).getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days <= 0) return { label: "Active today", tone: "ok" };
  if (days <= 7) return { label: `${days}d ago`, tone: "ok" };
  if (days <= 30) return { label: `${days}d ago`, tone: "warn" };
  return { label: `${days}d ago`, tone: "stale" };
}

export function TeamSubscriptionAudit() {
  const { mode, role, dealershipId, isSolo } = useAuth();
  const { subscription, isActive } = useSubscription();
  const [rows, setRows] = useState<RepRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  const isDealershipPlan =
    subscription?.price_id === "dealership_monthly" ||
    subscription?.price_id === "dealership_yearly";

  useEffect(() => {
    if (mode !== "real" || !dealershipId) return;
    if (role !== "manager" && role !== "admin") return;
    if (isSolo || !isActive || !isDealershipPlan) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      // 1) All rep user_ids in the dealership
      const { data: roles } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .eq("dealership_id", dealershipId)
        .eq("role", "rep");
      const userIds = Array.from(new Set((roles ?? []).map((r) => r.user_id)));
      if (userIds.length === 0) {
        if (!cancelled) { setRows([]); setLoading(false); }
        return;
      }

      // 2) Profiles for names/emails
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", userIds);

      // 3) Last login from usage_events (this period — last 30d as proxy)
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data: events } = await supabase
        .from("usage_events")
        .select("user_id, created_at")
        .eq("dealership_id", dealershipId)
        .eq("event_name", "login")
        .gte("created_at", since)
        .order("created_at", { ascending: false });

      const lastSeen = new Map<string, string>();
      (events ?? []).forEach((e) => {
        if (!lastSeen.has(e.user_id)) lastSeen.set(e.user_id, e.created_at);
      });

      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));
      const built: RepRow[] = userIds.map((uid) => {
        const prof = profileMap.get(uid);
        const last = lastSeen.get(uid) ?? null;
        const days = last
          ? Math.floor((Date.now() - new Date(last).getTime()) / (1000 * 60 * 60 * 24))
          : null;
        return {
          user_id: uid,
          full_name: prof?.full_name ?? null,
          email: prof?.email ?? null,
          last_seen_at: last,
          days_since: days,
        };
      }).sort((a, b) => {
        // Most stale (or never) first to surface prune candidates
        const aDays = a.days_since ?? 9999;
        const bDays = b.days_since ?? 9999;
        return bDays - aDays;
      });

      if (!cancelled) { setRows(built); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [mode, role, dealershipId, isSolo, isActive, isDealershipPlan]);

  if (mode !== "real") return null;
  if (role !== "manager" && role !== "admin") return null;
  if (isSolo || !isActive || !isDealershipPlan) return null;

  const stale = (rows ?? []).filter((r) => (r.days_since ?? 9999) > 30).length;
  const purchased = subscription?.quantity ?? 1;
  const used = rows?.length ?? 0;
  const couldPrune = Math.min(stale, Math.max(0, used - 1));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Users2 className="h-4 w-4 text-primary" /> Team subscription audit
        </CardTitle>
        <CardDescription>
          Reps not logging in still consume seats. Review before your next renewal.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !rows || rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No reps yet — invite your team from above.</p>
        ) : (
          <>
            {couldPrune > 0 && (
              <div className="flex items-start gap-2 text-xs p-3 rounded-md bg-amber-500/10 border border-amber-500/30">
                <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <span>
                  <span className="font-medium">{couldPrune}</span> rep{couldPrune > 1 ? "s have" : " has"} not logged in for 30+ days.
                  Removing them could save up to{" "}
                  <span className="font-medium">
                    ${(couldPrune * (subscription?.price_id === "dealership_yearly" ? 41 : 49)).toLocaleString()}
                  </span>
                  /{subscription?.price_id === "dealership_yearly" ? "mo equivalent" : "mo"} on next renewal.
                </span>
              </div>
            )}

            <div className="divide-y divide-border/60">
              {rows.map((r) => {
                const seen = formatLastSeen(r.last_seen_at);
                return (
                  <div key={r.user_id} className="flex items-center justify-between py-2.5 gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.full_name ?? r.email ?? "Unknown rep"}</p>
                      {r.email && r.full_name && (
                        <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      <Badge
                        variant={seen.tone === "ok" ? "secondary" : seen.tone === "warn" ? "outline" : "destructive"}
                        className="text-[10px]"
                      >
                        {seen.label}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground">
              {used} of {purchased} purchased seats in use. Remove reps from Team members above to free a seat.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}